import { useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { WorkflowCard, DetailDrawer, STAGE_ACCENTS } from "@/components/workflow/WorkflowBoard";
import { cardHasFlag } from "@/components/workflow/Watchtower";

const Ico = ({ name, className }) => { const C = Icons[name] || Icons.Circle; return <C className={className} />; };

// Each phase pulls from its own tested board endpoint and routes transitions to its own
// backend. Cards advance BETWEEN phases automatically, so cross-phase drag is blocked.
export const PHASES = [
  { key: "lead", label: "Sales · Lead", color: "#3b82f6", icon: "TrendingUp",
    endpoint: "/workflow/board/lead", cardBase: "/workflow/card", workspacePathFn: (id) => `/client-workspace/${id}`,
    transition: (card, toStage) => api.post("/workflow/transition", { entity_id: card.id, pipeline_key: "lead", to_stage: toStage, method: "drag_drop" }) },
  { key: "client", label: "Client · Onboarding", color: "#8b5cf6", icon: "HeartHandshake",
    endpoint: "/workflow/board/client", cardBase: "/workflow/card", workspacePathFn: (id) => `/client-workspace/${id}`,
    transition: (card, toStage) => api.post("/workflow/transition", { entity_id: card.id, pipeline_key: "client_onboarding", to_stage: toStage, method: "drag_drop" }) },
  { key: "production", label: "Production", color: "#10b981", icon: "Factory",
    endpoint: "/production/board", cardBase: "/production/board/card", workspacePathFn: (pid) => `/production/${pid}`,
    transition: (card, toStage) => api.post("/production/board/transition", { entity_id: card.id, to_stage: toStage, method: "drag_drop" }) },
];

export async function loadLifecycleBoards() {
  const results = await Promise.allSettled(PHASES.map((p) => api.get(p.endpoint)));
  const map = {};
  results.forEach((r, i) => { map[PHASES[i].key] = r.status === "fulfilled" ? r.value.data : { error: true }; });
  return map;
}

export default function LifecycleBoardView({ boards, setBoards, flagFilter, onClearFlag }) {
  const [drag, setDrag] = useState(null);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const [attention, setAttention] = useState(false);

  const matches = useCallback((c) => {
    if (q && !`${c.name} ${c.ref || ""} ${c.service || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (attention && !(c.badges?.unread || c.badges?.documents || c.badges?.payment_hold || c.badges?.overdue || c.badges?.allison || c.days_in_stage > (c.sla_days ?? 999))) return false;
    if (flagFilter && !cardHasFlag(c, flagFilter.key)) return false;
    return true;
  }, [q, attention, flagFilter]);

  const doTransition = async (phase, card, toStage) => {
    if (card.stage === toStage) return;
    const from = card.stage;
    setBoards((b) => ({ ...b, [phase.key]: { ...b[phase.key], cards: b[phase.key].cards.map((c) => c.id === card.id ? { ...c, stage: toStage } : c) } }));
    try {
      const r = await phase.transition(card, toStage);
      const percent = r?.data?.percent;
      setBoards((b) => ({ ...b, [phase.key]: { ...b[phase.key], cards: b[phase.key].cards.map((c) => c.id === card.id ? { ...c, stage: toStage, progress: percent ?? c.progress, days_in_stage: 0 } : c) } }));
    } catch (e) {
      setBoards((b) => ({ ...b, [phase.key]: { ...b[phase.key], cards: b[phase.key].cards.map((c) => c.id === card.id ? { ...c, stage: from } : c) } }));
      toast.error(e.response?.data?.detail || "Move blocked", { description: "The card was returned to its original stage." });
    }
  };

  const onDrop = (phase, colKey) => {
    if (!drag) return;
    if (drag.phaseKey !== phase.key) {
      toast.info("Cards move between phases automatically", { description: "A lead becomes a client when its engagement is signed; a client enters Production when it's marked Production Ready." });
      setDrag(null);
      return;
    }
    doTransition(phase, drag.card, colKey);
    setDrag(null);
  };

  return (
    <div data-testid="lifecycle-board">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative"><Icons.Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-2.5" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search across all phases…" className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg w-64" data-testid="lc-filter-search" /></div>
        <button onClick={() => setAttention(!attention)} className={`text-sm rounded-lg px-3 py-1.5 border flex items-center gap-1.5 ${attention ? "bg-amber-50 border-amber-300 text-amber-700" : "border-slate-200 text-slate-500"}`} data-testid="lc-filter-attention"><Icons.AlertTriangle className="h-3.5 w-3.5" />Stalled &amp; needs attention</button>
        {flagFilter && <button onClick={onClearFlag} data-testid="lc-flag-chip" className="text-sm rounded-lg px-3 py-1.5 border bg-violet-50 border-violet-300 text-violet-700 flex items-center gap-1.5"><Icons.Sparkles className="h-3.5 w-3.5" />Allison: {flagFilter.label}<Icons.X className="h-3.5 w-3.5" /></button>}
        <div className="ml-auto flex items-center gap-3 text-[10px] text-slate-400" data-testid="lc-heat-legend">
          <span className="flex items-center gap-1"><Icons.Flame className="h-3 w-3 text-slate-400" />Stall aging:</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: "#f59e0b" }} />Past SLA</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: "#f97316" }} />Stalling</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: "#ef4444" }} />Severe</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="flex gap-6 min-w-max pb-2">
          {PHASES.map((phase) => {
            const board = boards[phase.key];
            const cards = (board?.cards || []).filter(matches);
            const columns = board?.columns || [];
            const phaseStalled = cards.filter((c) => c.stage !== "completed" && c.days_in_stage > (c.sla_days ?? 999)).length;
            return (
              <div key={phase.key} className="flex flex-col" data-testid={`lc-phase-${phase.key}`}>
                <div className="mb-2 rounded-lg px-3 py-2 flex items-center justify-between" style={{ backgroundColor: `${phase.color}12`, borderLeft: `4px solid ${phase.color}` }}>
                  <span className="flex items-center gap-2 text-sm font-bold" style={{ color: phase.color }}><Ico name={phase.icon} className="h-4 w-4" />{phase.label}</span>
                  <span className="flex items-center gap-1.5">
                    {phaseStalled > 0 && <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5" title="Cards past SLA in this phase">{phaseStalled} ⚠</span>}
                    <span className="text-[11px] font-medium text-slate-500 bg-white rounded-full px-2 py-0.5">{cards.filter((c) => c.stage !== "completed").length}</span>
                  </span>
                </div>
                {board?.error ? (
                  <div className="text-xs text-slate-400 px-3 py-6">You don't have access to this phase.</div>
                ) : (
                  <div className="flex gap-3">
                    {columns.map((col, idx) => {
                      const accent = STAGE_ACCENTS[idx % STAGE_ACCENTS.length];
                      const colCards = cards.filter((c) => c.stage === col.key);
                      const breached = colCards.filter((c) => c.days_in_stage > (col.sla_days ?? 999)).length;
                      return (
                        <div key={col.key} className="w-[248px] shrink-0 flex flex-col bg-slate-50/70 rounded-xl" data-testid={`lc-column-${phase.key}-${col.key}`}
                          onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(phase, col.key)}>
                          <div className={`px-3 py-2 flex items-center justify-between rounded-t-xl border-t-2 ${breached ? "bg-amber-50" : ""}`} style={{ borderColor: breached ? "#f59e0b" : accent }}>
                            <span className={`text-[11px] font-semibold uppercase tracking-wide truncate flex items-center gap-1 ${breached ? "text-amber-700" : "text-slate-700"}`}>
                              {breached > 0 && <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" /></span>}
                              {col.label}
                            </span>
                            <span className="text-[11px] font-medium text-slate-400 bg-white rounded-full px-2 py-0.5">{colCards.length}</span>
                          </div>
                          <div className="px-2 pb-2 max-h-[520px] overflow-y-auto min-h-[100px]">
                            {colCards.map((c) => (
                              <WorkflowCard key={c.id} card={c} accent={accent} showHeat
                                onOpen={(cd) => setOpen({ id: cd.id, base: phase.cardBase, workspaceFn: phase.workspacePathFn })}
                                onDragStart={() => setDrag({ card: c, phaseKey: phase.key })}
                                dragging={drag?.card?.id === c.id} />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {open && <DetailDrawer cardId={open.id} onClose={() => setOpen(null)} cardEndpointBase={open.base} workspacePathFn={open.workspaceFn} />}
    </div>
  );
}
